import { useEffect, useMemo, useState } from 'react';
import CommandBarFallback from './components/CommandBarFallback';
import { classifyIntent } from './classifyIntent';
import { getLayoutVariant } from './componentRegistry';
import type {
  IntentClassification,
  IntentContext,
  LayoutVariant,
  TelemetryCallbacks,
  UserSignal,
} from './types';

interface GenerativeLayoutEngineProps extends TelemetryCallbacks {
  initialQuery?: string;
  initialSignals?: UserSignal[];
  query?: string;
  signals?: UserSignal[];
  userGoal?: string;
  activeWorkspace?: string;
  fallbackMessage?: string;
  idleTimeoutMs?: number;
  onQueryChange?: (query: string) => void;
  onEmitSignal?: (signal: UserSignal) => void;
  resolveVariant?: (classification: IntentClassification) => LayoutVariant;
}

const LOW_CONFIDENCE_THRESHOLD = 0.55;

const GenerativeLayoutEngine = ({
  initialQuery = '',
  initialSignals = [],
  query: controlledQuery,
  signals: controlledSignals,
  userGoal = 'Help the user move from understanding to execution.',
  activeWorkspace = 'intent-demo',
  fallbackMessage,
  idleTimeoutMs = 30000,
  onQueryChange,
  onEmitSignal,
  onIntentSuccess,
  onIntentFriction,
  resolveVariant,
}: GenerativeLayoutEngineProps) => {
  const [internalQuery, setInternalQuery] = useState(initialQuery);
  const [internalSignals, setInternalSignals] = useState<UserSignal[]>(initialSignals);

  const query = controlledQuery ?? internalQuery;
  const signals = controlledSignals ?? internalSignals;

  const setQuery = (nextQuery: string) => {
    if (controlledQuery === undefined) {
      setInternalQuery(nextQuery);
    }
    onQueryChange?.(nextQuery);
  };

  const context = useMemo<IntentContext>(
    () => ({
      query,
      userGoal,
      signals,
      activeWorkspace,
    }),
    [activeWorkspace, query, signals, userGoal]
  );

  const { classification, error } = useMemo<{ classification: IntentClassification; error: string | null }>(() => {
    try {
      return {
        classification: classifyIntent(context),
        error: null,
      };
    } catch (caughtError) {
      return {
        classification: {
          intent: 'NAVIGATIONAL',
          confidence: 0.2,
          reasoning: ['Intent engine failed while processing the current context.'],
          supportingSignals: signals,
        },
        error: caughtError instanceof Error ? caughtError.message : 'Unknown intent engine failure',
      };
    }
  }, [context, signals]);

  const emitSignal = (signal: UserSignal) => {
    if (controlledSignals === undefined) {
      setInternalSignals((current) => [...current, signal]);
    }
    onEmitSignal?.(signal);

    if (signal.type === 'submit') {
      onIntentSuccess?.({
        classification,
        action: signal.type,
        metadata: signal.metadata,
      });
    }

    if (signal.type === 'cancel' || signal.type === 'idle') {
      onIntentFriction?.({
        classification,
        action: signal.type,
        metadata: signal.metadata,
      });
    }
  };

  useEffect(() => {
    const idleTimer = window.setTimeout(() => {
      onIntentFriction?.({
        classification,
        action: 'idle',
        metadata: { source: 'intent-layout-engine' },
      });
    }, idleTimeoutMs);

    return () => window.clearTimeout(idleTimer);
  }, [classification, idleTimeoutMs, onIntentFriction]);

  if (error || classification.confidence < LOW_CONFIDENCE_THRESHOLD) {
    return (
      <CommandBarFallback
        message={
          error
            ? `The intent engine failed gracefully: ${error}`
            : fallbackMessage || 'Intent confidence is low, so the system is falling back to a universal command surface.'
        }
        query={query}
        onQueryChange={setQuery}
      />
    );
  }

  const variant = resolveVariant?.(classification) || getLayoutVariant(classification.intent);
  const slotProps = { classification, context, emitSignal };

  return (
    <div className="space-y-6">
      {variant.slots.hero?.(slotProps)}

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1.5fr)_minmax(320px,1fr)]">
        <div className="space-y-6">
          {variant.slots.primary?.(slotProps)}
          {variant.slots.actions?.(slotProps)}
        </div>

        <div className="space-y-6">
          {variant.slots.secondary?.(slotProps)}
          {variant.slots.support?.(slotProps)}
        </div>
      </div>
    </div>
  );
};

export default GenerativeLayoutEngine;